import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { launchConfetti } from "../../utils/confetti";
import BonusRouter from "./BonusRouter"; // Ensure the path is correct
import "./MatchingQuiz.css";
import { sfx } from "./sfx";

const MatchingQuiz = ({ questions, onComplete, topic }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [score, setScore] = useState(0);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [showBonus, setShowBonus] = useState(false);
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [bonusScore, setBonusScore] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  const correctSound = sfx("https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306");
  const incorrectSound = sfx("https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686");

  const current = questions[currentIndex];
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const passingScore = Math.ceil(questions.length * 0.8);

  useEffect(() => {
    const lefts = current.pairs.map((p) => p.left);
    const rights = current.pairs.map((p) => p.right);
    setLeftItems(shuffle(lefts));
    setRightItems(shuffle(rights));
    setSelectedLeft(null);
    setSelectedRight(null);
    setMatchedPairs([]);
  }, [currentIndex]);

  const shuffle = (array) => [...array].sort(() => Math.random() - 0.5);

  const handleLeftClick = (item) => {
    setSelectedLeft(item);
  };

  const handleRightClick = (item) => {
    setSelectedRight(item);

    if (selectedLeft && selectedLeft.text === item.text) {
      correctSound.play();
      setMatchedPairs((prev) => [...prev, item.text]);
      setSelectedLeft(null);
      setSelectedRight(null);
    } else {
      incorrectSound.play();
      setTimeout(() => {
        setSelectedLeft(null);
        setSelectedRight(null);
      }, 1000);
    }
  };

  const isComplete = rightItems.every((r) => matchedPairs.includes(r.text));

  useEffect(() => {
    if (isComplete) {
      setScore((prev) => prev + 1);
      setTimeout(() => {
        if (currentIndex < questions.length - 1) {
          setCurrentIndex((i) => i + 1);
        } else {
          if (score + 1 >= passingScore) {
            launchConfetti();
            setShowBonus(true);
          } else {
            onComplete(score + 1);
          }
        }
      }, 1000);
    }
  }, [matchedPairs]);

  if (showBonus) {
    return (
      <BonusRouter
        slug={topic.slug} // or topic.bonusType
        questions={questions}
        onComplete={(bonusScore) => {
          setBonusScore(bonusScore);
          setShowBonus(false);
          setShowFinalResults(true);
        }}
      />
    );
  }

  if (showFinalResults) {
    return (
      <div style={{ padding: 20 }}>
        <h3>Final Results</h3>
        <p>Score: {score}</p>
        <p>Bonus Score: {bonusScore}</p>
        <p>Total Score: {score + bonusScore}</p>
      </div>
    );
  }

  return (
    <div className="matching-quiz-wrapper">
      {/* Scoreboard */}
      <div className="scoreboard">
        <img src="/assets/scoreboard-background.png" alt="Scoreboard" className="scoreboard-bg" />
        <div className="score-overlay">
          <p>Score: {score}</p>
          {bonusScore !== undefined && <p>Bonus: {bonusScore}</p>}
        </div>
      </div>
  
      {/* Compass Return Button */}
      <img
        src="/assets/compass-return2.png"
        alt="Return Home"
        className="compass-return"
        onClick={() => setShowConfirm(true)}
      />
  
      {/* Confirmation Popup */}
      {showConfirm && (
        <div className="confirm-overlay">
          <img src="/assets/confirmation-barrel.png" alt="Confirm Exit" className="confirm-barrel" />
          <div className="confirm-message">
            <p>Are you sure you want to leave?</p>
            <p>If you leave now you will lose all the progress that you have made so far.</p>
            <button onClick={() => onComplete(score)}>Yes</button>
            <button onClick={() => setShowConfirm(false)}>No</button>
          </div>
        </div>
      )}
  
      {/* Game Content */}
      <div className="matching-quiz-container">
        <img 
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="SBL Logo"
          className="drag-quiz-logo"
        />
  
        <h3>{current.prompt}</h3>
        <div style={{ display: "flex", gap: 60, marginTop: 30 }}>
          {/* Column A */}
          <div>
            <h4>Column A</h4>
            {leftItems.map((item, i) => (
              <motion.button
                key={i}
                className="matching-button"
                onClick={() => handleLeftClick(item)}
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
                animate={{
                  backgroundColor: selectedLeft === item ? "#e0f7fa" : "#f0f0f0",
                }}
                transition={{ duration: 0.3 }}
              >
                {item.image && <img src={item.image} alt={item.text} />}
                {item.text}
              </motion.button>
            ))}
          </div>
  
          {/* Column B */}
          <div>
            <h4>Column B</h4>
            {rightItems.map((item, i) => (
              <motion.button
                key={i}
                className="matching-button"
                onClick={() => handleRightClick(item)}
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
                animate={{
                  backgroundColor: selectedLeft === item ? "#e0f7fa" : "#f0f0f0",
                }}
                transition={{ duration: 0.3 }}
              >
                {item.image && <img src={item.image} alt={item.text} />}
                {item.text}
              </motion.button>
            ))}
          </div>
        </div>
  
        <div className="footer">
          © Smart Board Learning LLC. All rights reserved.
        </div>
      </div>
    </div>
  );
  
};

export default MatchingQuiz;
